import DOMPurify from 'dompurify';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const DANGEROUS_CSS_PATTERN = /(?:url\s*\(|expression\s*\(|@import|-moz-binding)/i;
const CSS_RESOURCE_FUNCTION_PATTERN = /(?:(?:-webkit-)?(?:image-set|cross-fade)|image|src)\s*\(/i;
const CSS_URL_PATTERN = /url\s*\(\s*(['"]?)(.*?)\1\s*\)/gi;
const SVG_REFERENCE_ATTRIBUTES = new Set([
    'fill', 'stroke', 'clip-path', 'mask', 'filter',
    'marker-start', 'marker-mid', 'marker-end',
]);
const SAFE_HTML_CLASS_PATTERNS = [
    /^language-[\w-]+$/,
    /^(?:math|inline|display|math-placeholder|cxsmiles-placeholder|mention-chip)$/,
    /^mention-(?:template|style|prior_art|file|document|figure|blocks)$/,
];
const SAFE_INLINE_STYLE_PROPERTIES = new Set([
    'color', 'background-color',
    'font-family', 'font-size', 'font-style', 'font-variant', 'font-weight',
    'line-height', 'letter-spacing', 'word-spacing',
    'text-align', 'text-decoration', 'text-decoration-color', 'text-decoration-style',
    'text-decoration-thickness', 'text-indent', 'text-transform',
    'white-space', 'word-break', 'overflow-wrap', 'vertical-align',
    'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
    'border-color', 'border-style', 'border-width', 'border-collapse', 'border-spacing',
    'width', 'min-width', 'max-width', 'height', 'min-height', 'max-height',
]);

const ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    '\'': '&#39;',
};

export function escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, (c) => ESCAPE_MAP[c]);
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
    }
    // <use> 的引用必须是本文档内的 # 片段。ALLOWED_URI_REGEXP 拦得住 http:/data:
    // 这类带 scheme 的值，但放行 `/x.svg#id` 与 `//evil.com/x.svg#id`——前者不含
    // 冒号、后者以非字母开头，两条都能满足那个正则，却都会去取外部文档。
    if (node.namespaceURI === SVG_NAMESPACE) {
        for (const attribute of Array.from(node.attributes)) {
            const mayContainReference = SVG_REFERENCE_ATTRIBUTES.has(attribute.name);
            if (
                containsExternalCssReference(attribute.value)
                || containsCssObfuscation(attribute.value)
                || (mayContainReference && CSS_RESOURCE_FUNCTION_PATTERN.test(attribute.value))
            ) {
                node.removeAttribute(attribute.name);
            }
        }
        for (const attributeName of ['href', 'xlink:href']) {
            const value = node.getAttribute(attributeName);
            if (value !== null && !value.startsWith('#')) {
                node.removeAttribute(attributeName);
            }
        }
    }
    if (node.namespaceURI !== SVG_NAMESPACE) {
        const className = node.getAttribute('class');
        if (className) {
            const safeClassNames = className
                .split(/\s+/)
                .filter(name => SAFE_HTML_CLASS_PATTERNS.some(pattern => pattern.test(name)));
            if (safeClassNames.length > 0) node.setAttribute('class', safeClassNames.join(' '));
            else node.removeAttribute('class');
        }
    }
    const style = node.getAttribute('style');
    if (style) {
        const safeStyle = sanitizeInlineStyle(style, node.ownerDocument);
        if (safeStyle) node.setAttribute('style', safeStyle);
        else node.removeAttribute('style');
    }
});

function containsCssObfuscation(value: string): boolean {
    // CSS 转义与注释都能拆分关键字，例如 u\72l(...)、u/**/rl(...)。
    // 这里不尝试实现一套 CSS tokenizer：富文本样式和 SVG 引用遇到混淆语法时直接失败关闭。
    return value.includes('\\') || value.includes('/*');
}

function containsExternalCssReference(value: string): boolean {
    CSS_URL_PATTERN.lastIndex = 0;
    for (const match of value.matchAll(CSS_URL_PATTERN)) {
        if (!match[2].trim().startsWith('#')) return true;
    }
    return false;
}

function sanitizeInlineStyle(style: string, ownerDocument: Document): string {
    if (
        DANGEROUS_CSS_PATTERN.test(style)
        || CSS_RESOURCE_FUNCTION_PATTERN.test(style)
        || containsCssObfuscation(style)
    ) {
        return '';
    }

    const parsedStyle = ownerDocument.createElement('span').style;
    parsedStyle.cssText = style;
    const safeDeclarations: string[] = [];
    for (let index = 0; index < parsedStyle.length; index += 1) {
        const property = parsedStyle.item(index).toLowerCase();
        if (!SAFE_INLINE_STYLE_PROPERTIES.has(property)) continue;
        const value = parsedStyle.getPropertyValue(property);
        if (!value) continue;
        safeDeclarations.push(`${property}: ${value}`);
    }
    return safeDeclarations.join('; ');
}

const RICH_CONFIG = {
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
    ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
        'ul', 'ol', 'li', 'blockquote', 'hr',
        'code', 'pre', 'a', 'img', 'span', 'div',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'sup', 'sub', 'mark',
        'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'ms', 'mtext',
        'msup', 'msub', 'msubsup', 'mfrac', 'msqrt', 'mroot',
        'mtable', 'mtr', 'mtd', 'annotation',
    ],
    ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'target', 'rel',
        'colspan', 'rowspan', 'align', 'aria-hidden', 'style',
        'encoding', 'xmlns', 'display',
        'data-cxsmiles', 'data-cxsmiles-index', 'data-cxsmiles-tag-seq', 'data-has-cdx',
        // 正文素材（chemStructure 节点）的 insert_html 仍带 CDX 原文，与附件占位节点不同源
        'data-cdx',
        // 公式占位节点：KaTeX 在 sanitize 之后才渲染，所以只需放开占位节点自身的属性
        'data-math-latex', 'data-math-display',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

const INLINE_CONFIG = {
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
    ALLOWED_TAGS: [
        'br', 'strong', 'b', 'em', 'i', 'u', 's',
        'code', 'span', 'sup', 'sub', 'mark', 'a',
    ],
    ALLOWED_ATTR: [
        'href', 'title', 'class', 'target', 'rel',
        'aria-hidden',
        'data-mention-id', 'data-mention-name', 'data-mention-type',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export function sanitizeRichHtml(html: string): string {
    return DOMPurify.sanitize(html, RICH_CONFIG);
}

/**
 * 化学结构 SVG 的白名单。
 *
 * 附件的结构 SVG 从「本地算完自己用」变成「A 用户回写、B 用户读取」后，信任边界就变了，
 * 必须当作不可信输入。后端落库前会 sanitize 一次，这里是渲染前的第二道——
 * innerHTML 写的是当前页面，前端不能把安全性完全外包。
 *
 * foreignObject 能内嵌任意 HTML，等于绕开整个白名单，必须禁掉；
 * <use xlink:href="#id"> 这类结构内部引用照常工作，外链（可触发请求、
 * 泄露 referer）一律拦掉——ALLOWED_URI_REGEXP 拦 scheme 形式，
 * afterSanitizeAttributes 钩子补上无 scheme 的 /x.svg#id、//host/x.svg#id。
 */
const SVG_CONFIG = {
    ALLOW_ARIA_ATTR: false,
    ALLOW_DATA_ATTR: false,
    USE_PROFILES: { svg: true, svgFilters: true },
    // svg profile 不含 use，不显式放行就会被静默删掉。当前 Marvin 的产物只用
    // <linearGradient> + stroke="url(#id)"，没有 <use>，但 SVG 复用图元本就该走它，
    // 换个 exporter 配置或版本就可能出现——真出现时被悄悄删掉只会得到一张缺了一
    // 部分的结构图，不报错也不空白，很难查。href 由 afterSanitizeAttributes 钩子
    // 限死在 # 片段，见上面的说明。
    ADD_TAGS: ['use'],
    FORBID_TAGS: ['script', 'style', 'foreignObject', 'a', 'image'],
    // USE_PROFILES 自带的白名单里有 style/class，而 ALLOWED_ATTR 是并集不是覆盖，
    // 所以「不列出」挡不住。class 会复用宿主页全局 Tailwind 样式，也必须禁止。
    FORBID_ATTR: ['style', 'class'],
    // USE_PROFILES 只放行标签，属性必须单独列——漏了属性会得到一堆空标签骨架，
    // 画布尺寸(width/viewBox)与路径数据(d/points)全在属性里，结构会渲染成空白框。
    ALLOWED_ATTR: [
        // 画布与坐标系
        'xmlns', 'version', 'width', 'height', 'viewBox', 'preserveAspectRatio',
        // 几何
        'd', 'points', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
        'dx', 'dy', 'transform', 'gradientTransform', 'gradientUnits', 'offset',
        // 描边与填充
        'fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width', 'stroke-opacity',
        'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
        'stroke-miterlimit', 'opacity', 'color', 'stop-color', 'stop-opacity',
        // 文本（元素符号、基团标签）
        'font-family', 'font-size', 'font-weight', 'font-style',
        'text-anchor', 'dominant-baseline', 'alignment-baseline', 'letter-spacing',
        // <use> 上的 href 由 afterSanitizeAttributes 钩子限死在 # 片段
        // style 见上面的 FORBID_ATTR：它能带着 url(http://…) 绕过 ALLOWED_URI_REGEXP
        'id', 'href', 'xlink:href', 'clip-path', 'clip-rule', 'mask',
    ],
    // 这个正则会校验**所有**属性值，不止 href：写成 /^#/ 会把 width="200"、
    // d="M10 10L50 50" 一并判为非法丢掉，结构渲染成空白框。
    // 所以放开 # 片段与不含协议的普通字面值，只拦 scheme 形式的外链
    // （http:、data:、javascript: 等，可发请求、泄露 referer 或执行脚本）。
    ALLOWED_URI_REGEXP: /^(?:#|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/** 清洗化学结构 SVG，供 innerHTML 写入前调用。 */
export function sanitizeChemSvg(svg: string): string {
    if (!svg) return '';
    return DOMPurify.sanitize(svg, SVG_CONFIG);
}

const HTML_SANITIZERS = {
    inline: (html: string) => DOMPurify.sanitize(html, INLINE_CONFIG),
    rich: sanitizeRichHtml,
    'chem-svg': sanitizeChemSvg,
};

export type HtmlSanitizationPolicy = keyof typeof HTML_SANITIZERS;

export function isHtmlSanitizationPolicy(value?: string): value is HtmlSanitizationPolicy {
    return typeof value === 'string'
        && Object.prototype.hasOwnProperty.call(HTML_SANITIZERS, value);
}

/**
 * 按显式策略清洗待写入 DOM 的原始 HTML。
 *
 * @param html 待清洗的原始 HTML 字符串。
 * @param policy 内容所属的安全策略；未知策略会失败关闭并返回空串。
 * @returns 可安全写入当前页面的 HTML。
 */
export function sanitizeHtml(html: string, policy: HtmlSanitizationPolicy): string {
    if (!html || !isHtmlSanitizationPolicy(policy)) return '';
    return HTML_SANITIZERS[policy](html);
}

/**
 * 给化学结构 SVG 内部的 id 及其引用加上全局唯一前缀。
 *
 * 浏览器解析 `url(#id)` / `href="#id"` 时按整份 HTML 文档查找，不认 SVG 边界，
 * 命中的是文档里第一个同名 id。Marvin ImageExporter 对同一个结构总是产出同一串
 * SVG（id 也一样），所以「预览浮层」和「附件正文 OCR 面板」显示同一结构时 id 必然
 * 撞车，而正文那份在 display:none 容器里——Chrome 不为隐藏子树里的渐变生成绘制
 * 资源，于是引用过去拿到的是空渐变，那些用渐变上色的键就画不出来。切到原文 tab
 * 让正文可见，渐变生效，预览又"自己好了"，正是这个机制。
 *
 * Marvin 的实际产物用 <linearGradient> 给异原子键做双色渐变，靠 stroke="url(#id)"
 * 引用；不用 <use>。所以重写范围不能按属性名枚举——漏一个属性就意味着 id 改了、
 * 引用还指向旧名，引用断裂后那一段直接不画。这里统一处理：
 *   id="x"        -> id="<prefix>-x"
 *   url(#x)       -> url(#<prefix>-x)     （不限属性：stroke/fill/clip-path/mask/filter…）
 *   href="#x"     -> href="#<prefix>-x"   （含 xlink:href）
 *
 * 同一条要求也约束「序列化风格」这一维：定义与引用必须覆盖同样的写法集合，否则
 * 换一种等价写法就会一改一不改，重新回到引用断裂。SVG 的引号与 url() 内空白都是
 * 可选的，产物风格完全由 exporter 决定（renderMrvToSvg 原样透传其返回值，无后处理），
 * 所以三条规则统一接受单双引号，url() 内还容许可选空白与引号；改写后保留原引号风格，
 * 以免把 clip-path='url("#x")' 的内层引号换成单引号、与外层属性提前闭合。
 *
 * 仍是文本级改写，不解析 SVG：够用是因为 id 与片段引用都不允许含空白或引号，
 * 用不着完整解析器就能把值框准，也不必付 DOMParser 重新序列化改写属性大小写的风险。
 *
 * 必须在 sanitizeChemSvg 之后、写入 innerHTML 之前调用；
 * 不适合在 persistRenders 等回写后端的路径上使用（前缀不该持久化）。
 */
let svgUidCounter = 0;

// 属性值要求带引号（单双皆可），不接受无引号写法：XML/SVG 序列化器一律加引号，
// 而无引号的 `id=p/>` 无法区分自闭合斜杠与值本身，宁可不匹配也不要改出个 `p/`。
// url() 里的引号才是真可选——Marvin 现有产物正是裸 url(#x)。
// 值的字符类排除引号与空白：id 与片段引用本就不允许这两者，据此即可框准边界。
//
// 属性名左边界不能用 `\b`：`-` 与 `:` 都是非单词字符，`\bid` 会咬住 data-id 的尾部，
// 把无关属性也加上前缀（改坏比漏改更糟）。所以显式要求前面是空白或标签起始，
// 而 href 额外放行 `:` —— xlink:href 必须跟着改，它和 href 指向同一个片段。
const ID_ATTRIBUTE = /(^|[\s])id\s*=\s*(["'])([^"'\s]+)\2/g;
const URL_FRAGMENT = /\burl\(\s*(["']?)#([^"'\s)]+)\1\s*\)/g;
const HREF_FRAGMENT = /(^|[\s:])href\s*=\s*(["'])#([^"'\s]+)\2/g;

export function scopeChemSvgIds(svg: string): string {
    if (!svg) return svg;
    svgUidCounter += 1;
    const prefix = `chem-${svgUidCounter}`;
    return svg
        .replace(ID_ATTRIBUTE, (_m, before: string, quote: string, id: string) =>
            `${before}id=${quote}${prefix}-${id}${quote}`)
        // 不按属性名枚举：任何属性里的 url(#x) 都要跟着改，漏一个就引用断裂
        .replace(URL_FRAGMENT, (_m, quote: string, id: string) =>
            `url(${quote}#${prefix}-${id}${quote})`)
        // href / xlink:href 一并覆盖（xlink: 的 `:` 由左边界字符类放行，无需分两步）
        .replace(HREF_FRAGMENT, (_m, before: string, quote: string, id: string) =>
            `${before}href=${quote}#${prefix}-${id}${quote}`);
}
